import { useEffect, useMemo, useState } from "react";

import classNames from "classnames";
import { isPresent, hyphenate } from "neetocist";
import { Tooltip, Typography } from "neetoui";
import { isNil } from "ramda";
import { useTranslation } from "react-i18next";

import CopyToClipboardButton from "components/CopyToClipboardButton";
import TimeFormat from "components/TimeFormat";

import { BOOLEAN_VALUES_MAP, PHONE_NUMBER } from "./constants";
import EditField from "./EditField";
import { getIcon } from "./utils";

const Item = ({
  allowMultiline,
  handleCancel,
  handleSubmit,
  iconSize,
  item,
  showIcons,
}) => {
  const [isEditing, setIsEditing] = useState(item.isEditing);
  const [value, setValue] = useState(item.value);

  const { t } = useTranslation();

  const {
    icon,
    isEditable = false,
    extraClasses = "",
    extraWrapperClasses = "",
    id,
    fieldValueId,
    custom = false,
    name,
    kind,
    error,
    label,
    showCopyButton = false,
  } = item;

  const valueToRender = useMemo(() => {
    if (isNil(value)) return "-";

    if (typeof value === "boolean") return BOOLEAN_VALUES_MAP[value.toString()];

    if (name === "email") return value.toLowerCase();

    return value;
  }, [value]);

  const iconToRender = useMemo(
    () => icon || getIcon({ iconSize, name, value }),
    [icon, iconSize, name, value]
  );

  const onSuccess = () => setIsEditing(false);

  const handleMetadataSubmit = () =>
    handleSubmit({ fieldId: id, fieldValueId, name, value, custom, onSuccess });

  const handleMetadataCancel = () => {
    setIsEditing(false);
    setValue(item.value);
    handleCancel(name);
  };

  const handleChange = event => {
    const value = name === PHONE_NUMBER ? event : event.target.value;
    setValue(value);
  };

  useEffect(() => {
    setValue(item.value);
  }, [item]);

  const labelKey = hyphenate(name ?? label);

  return (
    <div
      data-testid={`metadata-${labelKey}-container`}
      className={classNames(
        "mb-3 flex place-items-center gap-2 align-middle",
        extraWrapperClasses,
        {
          "flex-wrap": name !== PHONE_NUMBER && allowMultiline,
          "flex-nowrap": !allowMultiline,
        }
      )}
    >
      {showIcons && isPresent(iconToRender) ? (
        <Tooltip
          content={
            label || t(`neetoMolecules.sessionEnvironment.details.keys.${name}`)
          }
        >
          <div className="flex-shrink-0">{iconToRender}</div>
        </Tooltip>
      ) : (
        <Typography
          className="neeto-molecules-session-environment-editable-field neeto-ui-text-gray-600 whitespace-nowrap"
          data-testid={`metadata-label-${labelKey}`}
          style="body2"
        >
          {label || t(`neetoMolecules.sessionEnvironment.details.keys.${name}`)}
        </Typography>
      )}
      {!isEditing ? (
        <>
          {kind === "datetime" ? (
            <TimeFormat.DateWeekTime
              date={value}
              typographyProps={{
                style: "body2",
                weight: "medium",
                className: classNames(
                  `overflow-hidden truncate whitespace-normal break-all ${extraClasses}`,
                  { "whitespace-nowrap": !allowMultiline }
                ),
              }}
            />
          ) : (
            <Tooltip content={valueToRender} position="top">
              <Typography
                data-testid={`metadata-value-${labelKey}`}
                style="body2"
                weight="medium"
                className={classNames(
                  `overflow-hidden truncate whitespace-normal break-all ${extraClasses}`,
                  {
                    "neeto-molecules-session-environment-editable-text cursor-pointer p-0.5":
                      isEditable,
                    "whitespace-nowrap": !allowMultiline,
                  }
                )}
                onClick={() => isEditable && setIsEditing(true)}
              >
                {valueToRender}
              </Typography>
            </Tooltip>
          )}
          {showCopyButton && (
            <CopyToClipboardButton
              className="flex-shrink-0"
              size="small"
              style="text"
              value={valueToRender}
            />
          )}
        </>
      ) : (
        <EditField
          {...{
            error,
            handleChange,
            handleMetadataCancel,
            handleMetadataSubmit,
            kind,
            label,
            name,
            value,
          }}
        />
      )}
    </div>
  );
};

export default Item;
